<template>
  <div>
    <page-header :title="$t('system.text_487')" />
    <steps class="my-3" v-model="step" />
    <page-body needMarginBottom>
      <component :is="stepComponent" :domain="domain" ref="stepForm" />
    </page-body>
    <page-footer>
      <template v-slot:right>
        <a-tooltip v-if="disabledJoinProject">
          <template slot="title">
            {{ $t('iam.disabled_join_project.tips') }}
          </template>
          <a-button type="primary" :loading="loading" :disabled="disabledJoinProject" @click="handleSubmit">{{ createTxt }}</a-button>
        </a-tooltip>
        <a-button v-else type="primary" :loading="loading" @click="handleSubmit">{{ createTxt }}</a-button>
        <a-button class="ml-3" @click="handleCancel">{{ cancelTxt }}</a-button>
      </template>
    </page-footer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import step from '@/mixins/step'
import workflowMixin from '@/mixins/workflow'
import { hasPermission } from '@/utils/auth'
import { WORKFLOW_TYPES } from '@/constants/workflow'
import { uuid } from '@/utils/utils'
import CreateUser from './form/CreateUser'
import JoinProject from './form/JoinProject'

export default {
  name: 'UserCreate',
  components: {
    CreateUser,
    JoinProject,
  },
  mixins: [step, workflowMixin],
  data () {
    return {
      loading: false,
      userMsg: {},
      step: {
        steps: [
          { title: this.$t('system.text_488'), key: 'create-user' },
          { title: this.$t('system.text_489'), key: 'join-project' },
        ],
        currentStep: 0,
      },
      domain: { key: 'default', label: 'Default' },
    }
  },
  computed: {
    ...mapGetters(['isAdminMode', 'l3PermissionEnable', 'userInfo', 'permission']),
    stepComponent () {
      return this.step.steps[this.step.currentStep].key
    },
    cancelTxt () {
      const _ = {
        'create-user': this.$t('system.text_491'),
        'join-project': this.$t('system.text_492'),
      }
      return _[this.stepComponent] || this.$t('system.text_491')
    },
    createTxt () {
      if (this.step.currentStep === 1 && this.isExecuteResourceOrderSetEnabled) {
        return this.$t('system.text_532')
      }
      return this.step.currentStep ? this.$t('system.text_498', []) : this.$t('common.create')
    },
    isExecuteResourceOrderSetEnabled () {
      return this.checkWorkflowEnabled(WORKFLOW_TYPES.EXECUTE_USER_JOIN)
    },
    disabledJoinProject () {
      if (this.step.currentStep === 1) {
        return !hasPermission({ key: 'projects_perform_join', permissionData: this.permission })
      }
      return false
    },
  },
  created () {
    this.manager = new this.$Manager('users', 'v1')
    if (this.l3PermissionEnable) {
      this.domain = {
        key: this.userInfo.projectDomainId,
        label: this.userInfo.projectDomain,
      }
    }
  },
  methods: {
    async handleSubmit () {
      this.loading = true
      try {
        const values = await this.$refs.stepForm.validateForm()
        if (this.step.currentStep === 0) {
          const { data } = await this.createUser(values)
          if (this.l3PermissionEnable) {
            this.domain = values.domain
          }
          this.userMsg = data
          this.step.currentStep = this.step.currentStep + 1
        } else if (this.step.currentStep === 1) {
          await this.joinProject(values)
          this.handleCancel()
        }
      } catch (error) {
        throw error
      } finally {
        this.loading = false
      }
    },
    async createUser (values) {
      try {
        const data = { ...values, skip_password_complexity_check: true }
        if (data.domain && data.domain.key) {
          data.domain_id = data.domain.key
          delete data.domain
        }
        if (!this.isAdminMode) {
          delete data.domain_id
        }
        // 格式化 expired_at 为 ISO 字符串，如果为空则设置为 null
        if (data.expired_at) {
          data.expired_at = this.$moment(data.expired_at).toISOString()
        }
        const ret = await this.manager.create({
          data,
        })
        this.$message.success(this.$t('system.text_493'))
        return ret
      } catch (error) {
        throw error
      }
    },
    async joinProject (values) {
      try {
        // 如果启用了 execute-user-join 工单类型，则创建工单
        if (this.isExecuteResourceOrderSetEnabled) {
          await this.joinProjectByWorkflow(values)
          this.$router.push('/workflow')
        } else {
          // 否则直接加入项目
          delete values.domain
          await this.manager.performAction({
            id: this.userMsg.id,
            action: 'join',
            data: values,
          })
          this.$message.success(this.$t('system.text_494'))
        }
      } catch (error) {
        throw error
      }
    },
    async joinProjectByWorkflow (values) {
      try {
        // 获取项目信息
        // projects 可能是字符串ID或数组，取第一个
        const projectId = Array.isArray(values.projects) ? values.projects[0] : values.projects
        const domainId = values.domain?.key || (typeof values.domain === 'string' ? values.domain : null) || this.domain?.key || 'default'
        const projectData = await this.$store.dispatch('storage/getProjectById', {
          key: projectId,
          project_domain: domainId,
        })

        // 创建 resource_order_sets
        const orderSetManager = new this.$Manager('resource_order_sets')
        const { displayname, name } = this.userInfo
        const projectDomainName = projectData?.project_domain || (domainId === 'default' ? 'Default' : domainId)
        const orderSetData = {
          auto_execute: false,
          name: this.$t('iam.user_join_project_workflow_name', [displayname || name, this.$moment().format('YYYY-MM-DD'), uuid()]),
          parameters: [{
            action: 'perform',
            perform_action_name: 'join',
            resource: 'users',
            resource_id: this.userMsg.id,
            project: projectId,
            project_domain: projectDomainName,
            parameter: {
              projects: values.projects,
              roles: values.roles,
            },
          }],
        }
        const orderSetRes = await orderSetManager.create({ data: orderSetData })

        // 创建工单
        const variables = {
          process_definition_key: WORKFLOW_TYPES.EXECUTE_USER_JOIN,
          initiator: this.userInfo.id,
          ids: orderSetRes.data.id,
          parameter: '{}',
          project: projectId,
          project_domain: projectData?.project_domain_id || domainId,
          execute_resource_order_set_type: 'users_perform_join',
        }
        await this.createWorkflow(variables)
        this.$message.success(this.$t('common.worflow_tip', [this.$t('system.text_487')]))
        this.$router.push('/workflow')
      } catch (error) {
        throw error
      }
    },
    handleCancel () {
      this.$router.push('/systemuser')
    },
  },
}
</script>
